import classNames from "classnames";
import { useMemo } from "react";
import { TransactionsPairForReconciliation } from "../../common/contracts/reconciliation.contract";
import {
    GridTable,
    GridTableHeader,
} from "../general/Tables/GridTable/GridTable";
import styles from "./Reconciliation.module.scss";
import { ReconciliationEmptyRow } from "./ReconciliationEmptyRow";
import { ReconciliationPair } from "./ReconciliationPair";
import { ReconciliationBalanceSummary } from "./ReconciliationBalanceSummary";
import { ReconciliationTransactionsSummary } from "./ReconciliationTransactionsSummary";
import { ReconciliationBalanceDifference } from "./ReconciliationBalanceDifference";

interface Props {
    pairs: TransactionsPairForReconciliation[];
    openingBalance: number;
    closingBalance: number;
    changeInBalance: number;
    cashIn: number;
    cashOut: number;
    net: number;
    difference: number;
}

const rowHeight = 40;
const headerHeight = 140;

export const ReconciliationTransactionsTable: React.FC<Props> = ({
    pairs,
    openingBalance,
    closingBalance,
    changeInBalance,
    cashIn,
    cashOut,
    net,
    difference,
}) => {
    const emptyRowsNumber =
        Math.floor((window.innerHeight - headerHeight) / rowHeight) -
        pairs.length;

    const noDifferences = useMemo(
        () => pairs.every((pair) => pair.fromAccount && pair.fromStatement),
        [pairs],
    );

    return (
        <GridTable
            noBorder
            className={classNames(styles.table, {
                [styles.tableWithoutDifferences]: noDifferences,
            })}
        >
            <GridTableHeader columnBorders className={styles.masterHeader}>
                <div className={styles.masterHeaderCell}>
                    <span className={styles.masterHeaderCellText}>
                        Bank statement
                    </span>
                    <ReconciliationBalanceSummary
                        openingBalance={openingBalance}
                        closingBalance={closingBalance}
                        changeInBalance={changeInBalance}
                    />
                </div>
                <div className={styles.masterHeaderCell}>
                    <span className={styles.masterHeaderCellText}>Kick</span>
                    <ReconciliationTransactionsSummary
                        cashIn={cashIn}
                        cashOut={cashOut}
                        net={net}
                    />
                </div>
                <div
                    className={classNames(
                        styles.masterHeaderCell,
                        styles.masterHeaderActionCell,
                    )}
                >
                    {!noDifferences && (
                        <span className={styles.masterHeaderCellText}>
                            Action
                        </span>
                    )}
                    {(!noDifferences || difference !== 0) && (
                        <ReconciliationBalanceDifference
                            difference={difference}
                        />
                    )}
                </div>
            </GridTableHeader>
            <GridTableHeader columnBorders>
                <div className={styles.headerCell}>Date</div>
                <div className={styles.headerCell}>Description</div>
                <div className={styles.headerCell}>Amount</div>

                <div className={styles.headerCell}>Date</div>
                <div className={styles.headerCell}>Description</div>
                <div className={styles.headerCell}>Amount</div>

                <div
                    className={classNames(
                        styles.headerCell,
                        styles.actionsCell,
                    )}
                />
            </GridTableHeader>
            {pairs.map((pair) => (
                <ReconciliationPair
                    key={
                        pair.fromAccount?.financialTransactionId ??
                        pair.fromStatement?.statementTransactionId
                    }
                    pair={pair}
                />
            ))}
            {Array.from({ length: emptyRowsNumber }).map(() => (
                <ReconciliationEmptyRow />
            ))}
        </GridTable>
    );
};
